import { useMsal } from '@azure/msal-react';
import {
  Flex,
  Grid,
  GridItem,
  ScaleFade,
  Text,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react';
import ClassItem from 'components/class/ClassItem';
import ClassItemLoading from 'components/class/ClassItemLoading';
import Breadcrumbs from 'components/common/Breadcrumbs';
import CourseItem from 'components/course/CourseItem';
import CourseItemLoading from 'components/course/CourseItemLoading';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getClassAction } from 'store/modules/class/class-action';
import {
  handleErrorAndNavigate,
  createErrorInfoFromAction,
} from 'utils/errorHandler';
import { ReducerStatus } from 'utils/constants';
import { ADMIN, TRAINER } from 'utils/constants/roles';
import { v4 as uuidv4 } from 'uuid';

function ClassPage() {
  const { accounts } = useMsal();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [isEditable, setIsEditable] = useState(false);

  const classObject = useSelector((state) => state.class.class);
  const getClass = useSelector((state) => state.class.getClass);

  const columnLayout = useBreakpointValue({
    base: '1fr',
    sm: '1fr 1fr',
    md: '1fr 1fr',
    lg: '1fr 1fr 1fr',
    xl: '1fr 1fr 1fr 1fr',
  });

  // Safe access to user roles with proper null/undefined checks
  // Memoize roles to prevent unnecessary re-renders in useEffect dependencies
  const roles = useMemo(() => {
    const userAccount = accounts?.[0];
    const idTokenClaims = userAccount?.idTokenClaims;
    return idTokenClaims?.roles || [];
  }, [accounts]);

  // Check if user has admin or trainer role for conditional navigation
  const hasAdminOrTrainerRole = roles.some((role) =>
    [ADMIN, TRAINER].includes(role)
  );
  // Use appropriate classes path based on user role for consistent navigation
  const classesPath = hasAdminOrTrainerRole ? '/admin/classes' : '/classes';

  // Configuration for ErrorDisplay component - memoized to prevent useEffect dependency changes
  const classEntityConfig = useMemo(
    () => ({
      name: 'Class',
      homeRoute: classesPath,
    }),
    [classesPath]
  );

  useEffect(() => {
    dispatch(getClassAction({ id, isAdminMode: false }));
  }, [id, dispatch]);

  useEffect(() => {
    if (classObject) {
      document.title = `Skill Quest | Class - ${classObject.name}`;
    }
  }, [classObject]);

  useEffect(() => {
    if (getClass.status === ReducerStatus.FAILED) {
      // Create standardized error info from Redux action failure
      const errorInfo = createErrorInfoFromAction(
        { payload: getClass.error },
        'Class Not Found',
        'The class you are looking for does not exist or may have been removed.'
      );

      // Handle error and navigate using utility function
      handleErrorAndNavigate(
        dispatch,
        navigate,
        location,
        errorInfo,
        classEntityConfig
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getClass.status, getClass.error, dispatch, navigate, classEntityConfig]);

  useEffect(() => {
    if (
      roles.some((role) => role === TRAINER || role === ADMIN) &&
      classObject?.isEditable
    ) {
      setIsEditable(true);
    } else {
      setIsEditable(false);
    }
  }, [roles, classObject]);

  return (
    <Flex ml={{ base: 0, md: 20 }} minH="100vh">
      <Flex width="100%" direction="column" p={10} gap={4}>
        <Flex mb={4} align="center" justify="space-between">
          <Breadcrumbs
            width="100%"
            links={[
              { name: 'Classes', path: classesPath },
              {
                name: classObject?.name || 'Loading...',
                path: `${classesPath}/${id}`,
              },
            ]}
          />
        </Flex>

        {getClass.status === ReducerStatus.LOADING || !classObject ? (
          <ClassItemLoading minH={200} w="100%" />
        ) : (
          <ScaleFade
            initialScale={0.9}
            in={!getClass.status !== ReducerStatus.LOADING}
          >
            <ClassItem
              minH={200}
              w="100%"
              classObject={classObject}
              isAdminMode={isEditable}
              isEditModeChecked={false}
            />
          </ScaleFade>
        )}

        {getClass.status !== ReducerStatus.LOADING &&
          classObject?.courses.length > 0 && (
            <Text mt={5} mb={3} fontWeight={500}>
              Courses:
            </Text>
          )}

        {getClass.status === ReducerStatus.LOADING || !classObject ? (
          <Grid templateColumns={columnLayout} gap={8}>
            {[...Array(12)].map(() => (
              <GridItem key={uuidv4()} w="100%">
                <CourseItemLoading />
              </GridItem>
            ))}
          </Grid>
        ) : (
          <ScaleFade
            initialScale={0.9}
            in={getClass.status !== ReducerStatus.LOADING && classObject}
          >
            <VStack align="stretch">
              <Grid templateColumns={columnLayout} gap={8}>
                {classObject.courses.map((course) => (
                  <GridItem w="100%" key={course.id}>
                    <CourseItem
                      id={course.id}
                      image={course.thumbnail}
                      category={course.category}
                      name={course.name}
                      description={course.description}
                      courseLink={`/classes/${id}/courses/${course.id}`}
                      isAdminMode={false}
                    />
                  </GridItem>
                ))}
              </Grid>
            </VStack>
          </ScaleFade>
        )}
      </Flex>
    </Flex>
  );
}

export default ClassPage;
